<template>
    <div class="section-form">
        <!-- Modo Solicitação de Carona -->
        <div v-if="isRideRequest" class="ride-request-form">
            <div class="ride-info-summary" v-if="selectedRide">
                <h6>Detalhes da Carona</h6>
                <div class="info-grid">
                    <div class="info-item">
                        <strong>Responsável:</strong>
                        <span>{{ selectedRide.user ? selectedRide.user.name : 'Não informado' }}</span>
                    </div>
                    <div class="info-item" v-if="selectedRide.driver">
                        <strong>Motorista:</strong>
                        <span>{{ selectedRide.driver.name }}</span>
                    </div>
                    <div class="info-item">
                        <strong>Veículo:</strong>
                        <span>{{ selectedRide.vehicles ? selectedRide.vehicles.description : 'Não informado' }}</span>
                    </div>
                    <div class="info-item">
                        <strong>Retirada:</strong>
                        <span>{{ selectedRide.retreat_date }}</span>
                    </div>
                    <div class="info-item">
                        <strong>Devolução:</strong>
                        <span>{{ selectedRide.devolution_date }}</span>
                    </div>
                </div>
            </div>

            <div class="message-section">
                <label for="ride-message">
                    <span class="required">*</span>
                    Mensagem para o responsável da reserva
                </label>
                <textarea
                    id="ride-message"
                    class="form-control"
                    rows="4"
                    :value="value.message"
                    @input="updateMessage"
                    placeholder="Olá! Gostaria de solicitar uma carona na sua viagem. Por favor, entre em contato comigo para combinarmos os detalhes."
                    required
                ></textarea>
                <small class="text-muted">
                    Explique o motivo da sua solicitação e forneça informações de contato se necessário.
                </small>
            </div>
        </div>

        <!-- Modo Normal de Reserva -->
        <div v-else class="shared-reservation-settings">
            <!-- Switch de Viagem Compartilhada -->
            <div class="row">
                <div class="col-md-6 my-auto">
                    <div class="form-group">
                        <div class="custom-control custom-switch">
                            <input
                                type="checkbox"
                                class="custom-control-input"
                                id="switch-shared-reservation"
                                :checked="value.shared_reservation"
                                @change="updateSharedReservation"
                            />
                            <label
                                class="custom-control-label"
                                for="switch-shared-reservation"
                            >
                                <span class="ml-3">A viagem poderá ser compartilhada com outros colaboradores</span>
                            </label>
                        </div>
                    </div>
                </div>

                <!-- Justificativa para não compartilhar -->
                <div
                    class="col-md-6"
                    v-if="!value.shared_reservation"
                >
                    <div class="form-group">
                        <label for="reason_not_shared">
                            <span class="required">*</span>
                            Justificativa
                        </label>
                        <textarea
                            class="form-control p-2"
                            id="reason_not_shared"
                            rows="1"
                            :value="value.reason_not_shared"
                            @input="updateReasonNotShared"
                            placeholder="Explique por que a viagem não será compartilhada"
                        ></textarea>
                    </div>
                </div>
            </div>

            <!-- Configurações do Motorista -->
            <div class="row">
                <div class="col-md-6 mb-3 my-auto">
                    <div class="form-group">
                        <div class="custom-control custom-switch">
                            <input
                                type="checkbox"
                                class="custom-control-input"
                                id="switch-is-driver"
                                :checked="value.is_driver"
                                @change="updateIsDriver"
                            />
                            <label
                                class="custom-control-label"
                                for="switch-is-driver"
                            >
                                <span class="mt-2 ml-3">
                                    O responsável por esta reserva será o motorista
                                </span>
                            </label>
                        </div>
                    </div>
                </div>

                <!-- Seleção de Motorista -->
                <div
                    class="col-md-3"
                    v-if="!value.is_driver"
                >
                    <div class="form-group">
                        <label for="driver">
                            <span class="required">*</span>
                            Selecione o motorista
                        </label>
                        <v-select
                            :options="filteredDrivers"
                            label="name"
                            :reduce="(driver) => driver.id"
                            class="style-chooser"
                            :value="value.driver_id"
                            @input="updateDriver"
                        >
                            <div slot="no-options">
                                <span class="mt-5">Não encontrou quem procurava?</span>
                                <button
                                    @click="$emit('toggle-driver-search')"
                                    type="button"
                                    class="btn btn-add btn-sm mt-2"
                                >
                                    <i class="fa fa-search mr-1"></i>
                                    <span>Buscar pela matrícula</span>
                                </button>
                            </div>
                        </v-select>
                    </div>
                </div>

                <!-- Busca de Motorista por Matrícula -->
                <div
                    v-if="isSearchingDriver"
                    class="col-md-3"
                >
                    <div class="form-group">
                        <label for="driver_registration">
                            <span class="required">*</span>
                            Digite a matrícula do motorista
                        </label>
                        <div class="input-group">
                            <div
                                class="input-group-prepend"
                                @click="$emit('search-driver')"
                            >
                                <span class="input-group-text clickable">
                                    <i
                                        class="fa fa-search"
                                        v-if="!isLoadingDriverSearch"
                                    ></i>
                                    <i
                                        class="fa fa-spinner fa-spin"
                                        v-else
                                    ></i>
                                </span>
                            </div>
                            <input
                                type="text"
                                class="form-control"
                                :value="driverRegistration"
                                @input="$emit('update:driver-registration', $event.target.value)"
                                @keyup.enter="$emit('search-driver')"
                                placeholder="Digite a matrícula"
                            />
                            <div
                                class="input-group-append"
                                @click="$emit('toggle-driver-search')"
                            >
                                <span class="input-group-text clickable">
                                    <i class="fa fa-times"></i>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div
                class="row"
            >
                <div class="col-md-6">
                    <div class="form-group">
                        <label for="passengers">Adicionar Passageiros</label>
                        <v-select
                            :options="filteredRides"
                            label="name"
                            :reduce="(ride) => ride.id"
                            class="style-chooser"
                            :value="value.ride"
                            @input="updateRides"
                            multiple
                        >
                            <div slot="no-options">
                                <span class="mt-5">Não encontrou quem procurava?</span>
                                <button
                                    @click="$emit('toggle-ride-search')"
                                    type="button"
                                    class="btn btn-add btn-sm mt-2"
                                >
                                    <i class="fa fa-search mr-1"></i>
                                    <span>Buscar pela matrícula</span>
                                </button>
                            </div>
                        </v-select>
                    </div>
                </div>

                <!-- Busca de Passageiro por Matrícula -->
                <div
                    v-if="isSearchingRide"
                    class="col-md-4"
                >
                    <div class="form-group">
                        <label for="ride_registration">
                            <span class="required">*</span>
                            Digite a matrícula do passageiro
                        </label>
                        <div class="input-group">
                            <div
                                class="input-group-prepend"
                                @click="$emit('search-ride')"
                            >
                                <span class="input-group-text clickable">
                                    <i
                                        class="fa fa-search"
                                        v-if="!isLoadingRideSearch"
                                    ></i>
                                    <i
                                        class="fa fa-spinner fa-spin"
                                        v-else
                                    ></i>
                                </span>
                            </div>
                            <input
                                type="text"
                                class="form-control"
                                :value="rideRegistration"
                                @input="$emit('update:ride-registration', $event.target.value)"
                                @keyup.enter="$emit('search-ride')"
                                placeholder="Digite a matrícula"
                            />
                            <div
                                class="input-group-append"
                                @click="$emit('toggle-ride-search')"
                            >
                                <span class="input-group-text clickable">
                                    <i class="fa fa-times"></i>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Info sobre passageiros selecionados -->
                <div
                    class="col-md-2"
                    v-if="value.ride && value.ride.length > 0"
                >
                    <div class="passengers-info">
                        <small class="text-muted">
                            {{ value.ride.length }} passageiro(s) selecionado(s)
                        </small>
                    </div>
                </div>
            </div>

            <!-- Aprovador (condicional) -->
            <div class="row" v-if="showApprover">
                <div class="col-md-3">
                    <div class="form-group">
                        <label for="approver">
                            <span class="required">*</span>
                            Aprovador
                        </label>
                        <v-select
                            :options="users"
                            label="name"
                            :reduce="(user) => user.id"
                            class="style-chooser"
                            :value="value.approver_id"
                            @input="updateApprover"
                        >
                            <div slot="no-options">
                                <span class="mt-5">
                                    Não encontrou quem procurava? Entre em contato com a administração
                                </span>
                            </div>
                        </v-select>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import vSelect from "vue-select";

export default {
    name: 'SharedReservationSettings',
    components: {
        vSelect,
    },
    model: {
        prop: 'value',
        event: 'input'
    },
    props: {
        value: {
            type: Object,
            required: true
        },
        drivers: {
            type: Array,
            default: () => []
        },
        rides: {
            type: Array,
            default: () => []
        },
        users: {
            type: Array,
            default: () => []
        },
        showApprover: {
            type: Boolean,
            default: false
        },
        isSearchingDriver: {
            type: Boolean,
            default: false
        },
        isSearchingRide: {
            type: Boolean,
            default: false
        },
        isLoadingDriverSearch: {
            type: Boolean,
            default: false
        },
        isLoadingRideSearch: {
            type: Boolean,
            default: false
        },
        driverRegistration: {
            type: String,
            default: ''
        },
        rideRegistration: {
            type: String,
            default: ''
        },
        isRideRequest: {
            type: Boolean,
            default: false
        },
        selectedRide: {
            type: Object,
            default: null
        }
    },
    computed: {
        excludedUserId() {
            return this.value.user_id || (this.$store.state.user ? this.$store.state.user.id : null);
        },

        filteredDrivers() {
            if (!this.excludedUserId) {
                return this.drivers;
            }

            return this.drivers.filter(driver => driver.id !== this.excludedUserId);
        },

        filteredRides() {
            // Se o responsável é o motorista, o usuário dono não pode ser passageiro
            if (this.value.is_driver) {
                return this.rides.filter(ride => ride.id !== this.excludedUserId);
            }

            // Se o responsável não é o motorista, o motorista selecionado não pode ser passageiro
            if (!this.value.is_driver && this.value.driver_id) {
                return this.rides.filter(ride => ride.id !== this.value.driver_id);
            }

            return this.rides;
        }
    },
    methods: {
        updateField(field, newValue) {
            const updatedValue = { ...this.value, [field]: newValue };
            this.$emit('input', updatedValue);
        },

        updateApprover(newValue) {
            this.updateField('approver_id', newValue);
        },

        updateSharedReservation(event) {
            const isShared = event.target.checked;
            const updatedValue = {
                ...this.value,
                shared_reservation: isShared,
                reason_not_shared: isShared ? '' : this.value.reason_not_shared
            };
            this.$emit('input', updatedValue);
        },

        updateReasonNotShared(event) {
            this.updateField('reason_not_shared', event.target.value);
        },

        updateIsDriver(event) {
            const isDriver = event.target.checked;
            let updatedRides = [...this.value.ride];

            // Se o responsável agora é o motorista, remove ele da lista de passageiros se estiver
            if (isDriver && this.excludedUserId) {
                updatedRides = updatedRides.filter(rideId => rideId !== this.excludedUserId);
            }

            const updatedValue = {
                ...this.value,
                is_driver: isDriver,
                driver_id: isDriver ? null : this.value.driver_id,
                ride: updatedRides
            };
            this.$emit('input', updatedValue);
        },

        updateDriver(newValue) {
            let updatedRides = [...this.value.ride];

            // Se um motorista foi selecionado, remove ele da lista de passageiros se estiver
            if (newValue && updatedRides.includes(newValue)) {
                updatedRides = updatedRides.filter(rideId => rideId !== newValue);
            }

            const updatedValue = {
                ...this.value,
                driver_id: newValue,
                ride: updatedRides
            };
            this.$emit('input', updatedValue);
        },

        updateRides(newValue) {
            this.updateField('ride', newValue);
            this.$emit('verify-max-places');
        },

        updateMessage(event) {
            this.updateField('message', event.target.value);
        },
    }
};
</script>

<style scoped>
.shared-reservation-settings {
    background-color: #ffffff;
    padding: 15px;
    border-radius: 6px;
    border: 1px solid #e9ecef;
    margin: 10px 0;
}

.input-group-text {
    background-color: #f8f9fa;
    border: 1px solid #ced4da;
}

.clickable {
    cursor: pointer;
    transition: background-color 0.15s ease-in-out;
}

.clickable:hover {
    background-color: #e9ecef;
}

.passengers-info {
    padding: 10px 0;
    display: flex;
    align-items: center;
    height: 100%;
}

.style-chooser {
    border-radius: 4px;
}

/* Estilos para modo solicitação de carona */
.ride-request-form {
    padding: 20px;
}

.ride-info-summary {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 15px;
    margin-bottom: 20px;
}

.ride-info-summary h6 {
    color: #495057;
    margin-bottom: 15px;
    font-weight: 600;
}

.info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 10px;
}

.info-item {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.info-item strong {
    color: #6c757d;
    font-size: 0.9rem;
}

.info-item span {
    color: #333;
    font-size: 0.95rem;
}

.message-section {
    margin-top: 20px;
}

.message-section label {
    font-weight: 500;
    margin-bottom: 10px;
    display: block;
}

.message-section textarea {
    resize: vertical;
    min-height: 100px;
}

.message-section small {
    display: block;
    margin-top: 5px;
}

/* Responsividade */
@media (max-width: 768px) {
    .shared-reservation-settings {
        padding: 10px;
    }

    .passengers-info {
        margin-top: 10px;
        text-align: center;
    }
}
</style>

<style>
/* Estilos globais para garantir que funcione em produção */
.vs--open .vs__dropdown-menu {
    z-index: 9999 !important;
}

.vs__dropdown-menu {
    z-index: 9999 !important;
}

/* Garantir que o v-select container não tenha overflow hidden */
.v-select {
    position: relative !important;
}

.v-select .vs__dropdown-menu {
    z-index: 9999 !important;
    position: absolute !important;
}
</style>



// WEBPACK FOOTER //
// src/components/ReserveVehicles/add-components/SharedReservationSettings.vue